import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAccount } from '@/contexts/AccountContext'
import { createEventMapping, updateEventMapping } from '../api/derivationConfig'
import type { EventMapping } from '../types'

interface MappingPair {
  intermediate: string
  coded: string
}

interface EventMappingModalProps {
  existing?: EventMapping
  onClose: () => void
}

function mappingsToPairs(mappings: Record<string, string>): MappingPair[] {
  return Object.entries(mappings).map(([intermediate, coded]) => ({ intermediate, coded }))
}

function pairsToMappings(pairs: MappingPair[]): Record<string, string> {
  const result: Record<string, string> = {}
  for (const pair of pairs) {
    if (pair.intermediate.trim()) {
      result[pair.intermediate.trim()] = pair.coded.trim()
    }
  }
  return result
}

export function EventMappingModal({ existing, onClose }: EventMappingModalProps) {
  const queryClient = useQueryClient()
  const { selectedAccountId } = useAccount()
  const isEdit = !!existing

  const [category, setCategory] = useState(existing?.category ?? '')
  const [eventName, setEventName] = useState(existing?.eventName ?? '')
  const [eventSource, setEventSource] = useState(existing?.eventSource ?? '')
  const [siteId, setSiteId] = useState(existing?.siteId ?? '')
  const [pairs, setPairs] = useState<MappingPair[]>(
    existing ? mappingsToPairs(existing.mappings) : [{ intermediate: '', coded: '' }],
  )
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null,
  )

  const mutation = useMutation({
    mutationFn: (data: EventMapping) => {
      if (isEdit && existing) {
        return updateEventMapping(
          existing.accId,
          existing.eventType,
          existing.category,
          existing.eventName,
          data,
        )
      }
      return createEventMapping(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-mappings'] })
      setFeedback({ type: 'success', message: isEdit ? 'Mapping updated.' : 'Mapping created.' })
      setTimeout(onClose, 800)
    },
    onError: (err: Error) => {
      setFeedback({ type: 'error', message: err.message })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const parsedAccId = parseInt(selectedAccountId ?? '', 10)
    if (isNaN(parsedAccId)) {
      setFeedback({ type: 'error', message: 'No account selected.' })
      return
    }
    mutation.mutate({
      accId: parsedAccId,
      eventType: 'custom',
      category: category.trim(),
      eventName: eventName.trim(),
      mappings: pairsToMappings(pairs),
      eventSource: eventSource.trim() || undefined,
      siteId: siteId.trim() || undefined,
    })
  }

  const addPair = () => setPairs((prev) => [...prev, { intermediate: '', coded: '' }])

  const removePair = (idx: number) =>
    setPairs((prev) => prev.filter((_, i) => i !== idx))

  const updatePair = (idx: number, field: 'intermediate' | 'coded', value: string) =>
    setPairs((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)))

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Event Mapping' : 'New Event Mapping'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <Input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. transaction"
                required
                disabled={isEdit}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Name</label>
              <Input
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="e.g. transaction_created"
                required
                disabled={isEdit}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Source</label>
              <Input
                value={eventSource}
                onChange={(e) => setEventSource(e.target.value)}
                placeholder="e.g. S2S"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site ID</label>
              <Input
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                placeholder="e.g. site_001"
              />
            </div>
          </div>

          {/* Field Mappings */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Field Mappings</label>
              <Button type="button" variant="outline" size="sm" onClick={addPair}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Field
              </Button>
            </div>
            <div className="space-y-2">
              {pairs.map((pair, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={pair.intermediate}
                    onChange={(e) => updatePair(idx, 'intermediate', e.target.value)}
                    placeholder="intermediate field (e.g. txn_id_orig)"
                    className="flex-1"
                  />
                  <span className="text-gray-400 text-sm shrink-0">→</span>
                  <Input
                    value={pair.coded}
                    onChange={(e) => updatePair(idx, 'coded', e.target.value)}
                    placeholder="coded field (e.g. s1)"
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => removePair(idx)}
                    className="text-gray-400 hover:text-red-500 transition-colors shrink-0"
                    disabled={pairs.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Feedback */}
          {feedback && (
            <p
              className={`text-sm ${feedback.type === 'success' ? 'text-green-600' : 'text-red-600'}`}
            >
              {feedback.message}
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Update Mapping' : 'Create Mapping'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
